import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Icon } from '@bloo-team/ui'
import { Database } from '@bloo-team/ui/icons'
import { bareField, displayValue, formatCell, type SearchRow } from '../../search'

/**
 * The field sidebar: every populated column with its top page-sample values
 * ALWAYS showing — no per-field expansion step, because the counts are local
 * (one pass over the rows on screen, no queries) and an investigator scans
 * the rail, not clicks through it. "count all" per field escalates to the
 * server aggregation, whose progress and result arrive through `server` and
 * replace that one field's local list. Clicking a value pivots exactly like
 * clicking a cell — the callbacks are the host's, so mode-awareness lives
 * there, not here.
 *
 * `dataModelPath` (S7.3, docs/data-model-requirements.md §Pivots): when the
 * host hands in a path, every field row carries a small "data model" link to
 * it with `?q=<field>` (bare — the `$` prefix is the query dialect's, not the
 * model's). A path, never a product import: core-shell stays product-blind.
 */
export function FieldSidebar({
  fields,
  facets,
  serverField,
  server,
  pivotEnabled,
  exactAvailable,
  dataModelPath,
  onPivotValue,
  onCountExact,
  onClose,
}: {
  fields: string[]
  /** Where the field → data-model reverse hop lands; no link when absent. */
  dataModelPath?: string
  /** field -> its top page-sample values, precomputed in one pass. */
  facets: ReadonlyMap<string, Array<[string, number]>>
  /** The one field whose SERVER exact counts are showing, if any. */
  serverField: string | null
  /** The facet job's render-relevant state, precomputed by the host. */
  server: {
    counting: boolean
    failed: boolean
    rows: SearchRow[]
    valueField: string | null
    countField: string | null
  }
  pivotEnabled: boolean
  exactAvailable: boolean
  onPivotValue: (field: string, value: unknown) => void
  onCountExact: (field: string) => void
  /** Hides the rail; the host's "fields" toggle brings it back. */
  onClose: () => void
}) {
  // Field-name filter. Local state, not lifted: it is a viewing aid for the
  // rail alone, nothing else needs to know, and it must survive a re-run
  // (fields prop changes) so a narrowed rail stays narrowed.
  const [query, setQuery] = useState('')
  const needle = query.trim().toLowerCase()
  const shown = needle ? fields.filter((f) => f.toLowerCase().includes(needle)) : fields
  return (
    <aside className="q-fields" aria-label="Result fields">
      <div className="q-fields-filter">
        <input
          type="search"
          className="q-fields-input"
          aria-label="Filter fields"
          placeholder="filter fields…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && query) {
              e.preventDefault()
              e.stopPropagation()
              setQuery('')
            }
          }}
        />
        <button type="button" className="q-fields-close" aria-label="Close fields" onClick={onClose}>
          ×
        </button>
      </div>
      {needle && shown.length === 0 ? (
        <span className="q-facet-note" role="status">no fields match</span>
      ) : null}
      {shown.map((f) => {
        const local = facets.get(f) ?? []
        return (
          <div key={f} className="q-field-group">
            <span style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <span className="q-field" style={{ flex: 1, width: 'auto', minWidth: 0 }}>{f}</span>
              {dataModelPath ? (
                <Link
                  to={dataModelPath}
                  search={{ q: bareField(f) } as Record<string, string>}
                  className="row-action"
                  aria-label={`Open ${bareField(f)} in the data model`}
                  title="data model"
                >
                  <Icon icon={Database} size={11} />
                </Link>
              ) : null}
            </span>
            <div className="q-facet">
              {serverField === f ? (
                server.counting ? (
                  <span className="q-facet-note" role="status">counting all matching…</span>
                ) : server.failed ? (
                  <span className="q-facet-note" role="status">could not count values</span>
                ) : server.valueField ? (
                  <>
                    {server.rows.map((r, i) => (
                      <button
                        key={i}
                        type="button"
                        className="q-facet-value"
                        disabled={!pivotEnabled}
                        onClick={() => onPivotValue(server.valueField!, r[server.valueField!])}
                      >
                        <span className="q-facet-v mono">
                          {formatCell(server.valueField!, r[server.valueField!])}
                        </span>
                        <span className="q-facet-n">
                          {server.countField ? formatCell(server.countField, r[server.countField]) : ''}
                        </span>
                      </button>
                    ))}
                    <span className="q-facet-note" role="status">all matching events</span>
                  </>
                ) : null
              ) : (
                <>
                  {local.length ? (
                    local.map(([v, n]) => (
                      <button
                        key={v}
                        type="button"
                        className="q-facet-value"
                        disabled={!pivotEnabled}
                        onClick={() => onPivotValue(f, v)}
                      >
                        <span className="q-facet-v mono">{displayValue(f, v)}</span>
                        <span className="q-facet-n">{n}</span>
                      </button>
                    ))
                  ) : (
                    <span className="q-facet-note" role="status">no values on this page</span>
                  )}
                  {exactAvailable ? (
                    <button type="button" className="q-facet-exact" onClick={() => onCountExact(f)}>
                      count all
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        )
      })}
    </aside>
  )
}
